import { CheckCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import AuthLayout from '../components/AuthLayout';
import { PasswordInput } from '../components/forms';
import { authService } from '../services/authService';

interface ResetPasswordFormData {
  password: string;
  confirmPassword: string;
}

const ResetPassword: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get email and OTP from navigation state
  const email = location.state?.email;
  const otp = location.state?.otp;
  const verified = location.state?.verified;

  useEffect(() => {
    // If no email, OTP, or not verified, redirect to forgot password
    if (!email || !otp || !verified) {
      navigate('/forgot-password');
    }
  }, [email, otp, verified, navigate]);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ResetPasswordFormData>();

  const password = watch('password');

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (!email || !otp) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.resetPassword(email, otp, data.password);

      if (response.success) {
        setIsSuccess(true);
      } else {
        setError(response.message || t('auth.passwordResetFailed'));
      }
    } catch {
      setError(t('auth.anErrorOccurred'));
    } finally {
      setIsLoading(false);
    }
  };

  // If no email, OTP, or not verified, this will redirect in useEffect
  if (!email || !otp || !verified) {
    return null;
  }

  return (
    <AuthLayout title={t('auth.resetPassword')} subtitle="" backTo="/forgot-password">
      {!isSuccess ? (
        <>
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Password field */}
            <div className="space-y-1">
              <label className="text-xs font-normal text-[#1F1F1F] leading-[1.6666666666666667em]">
                {t('auth.password')}
              </label>
              <PasswordInput
                {...register('password', {
                  required: t('auth.errors.passwordRequired'),
                  minLength: {
                    value: 8,
                    message: t('auth.errors.passwordMinLength', { length: 8 }),
                  },
                })}
                placeholder="**************"
                error={errors.password?.message}
                className="w-full border border-[#DEDEE4] rounded-md px-4 py-[14px] text-base font-normal text-[#6E7077] leading-[1.75em] focus:border-brand-primary focus:ring-brand-primary"
              />
            </div>

            {/* Confirm Password field */}
            <div className="space-y-1">
              <label className="text-xs font-normal text-[#1F1F1F] leading-[1.6666666666666667em]">
                {t('auth.confirmPassword')}
              </label>
              <PasswordInput
                {...register('confirmPassword', {
                  required: t('auth.errors.confirmPasswordRequired'),
                  validate: value => value === password || t('auth.errors.passwordsDoNotMatch'),
                })}
                placeholder="**************"
                error={errors.confirmPassword?.message}
                className="w-full border border-[#DEDEE4] rounded-md px-4 py-[14px] text-base font-normal text-[#6E7077] leading-[1.75em] focus:border-brand-primary focus:ring-brand-primary"
              />
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white font-bold py-[14px] px-[30px] rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm leading-[1.2102272851126534em] uppercase tracking-wide"
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  <span>{t('auth.changingPassword')}</span>
                </div>
              ) : (
                t('auth.changePassword').toUpperCase()
              )}
            </button>
          </form>
        </>
      ) : (
        /* Success message */
        <div className="text-center">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-6">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>

          <h2 className="text-3xl lg:text-[36px] font-bold text-[#1F1F1F] mb-4 leading-[1.3333333333333333em]">
            {t('auth.passwordResetSuccessfully')}
          </h2>

          <p className="text-base font-normal text-[#1F1F1F] leading-[1.75em] mb-8">
            {t('auth.passwordResetSuccess')}
          </p>

          <Link
            to="/login"
            className="block w-full bg-brand-primary hover:bg-brand-primary-hover text-white font-bold py-[14px] px-[30px] rounded-md transition-colors text-center text-sm leading-[1.2102272851126534em] uppercase tracking-wide"
          >
            {t('auth.signIn').toUpperCase()}
          </Link>
        </div>
      )}
    </AuthLayout>
  );
};

export default ResetPassword;
